import React, { useState } from 'react';
import { Plus, Clock } from 'lucide-react';
import { useAvailability, useCreateSchedule, useDeleteSchedule, useToggleScheduleActive, useUpdateSchedule } from '../../hooks/useAvailability';
import AvailabilityCalendar from '../../components/booking/AvailabilityCalendar';
import AvailabilityEditor from '../../components/booking/AvailabilityEditor';
import { BookingAvailabilityResponseDto, DayOfWeekEnum } from '../../types/booking';
import { AvailabilityFormValues } from '../../utils/bookingValidators';
import Spinner from '../../components/Spinner';
import { Link } from 'react-router-dom';
import { useBookingSettings } from '../../hooks/useBookingSettings';

export default function AvailabilityPage() {
  const { data: schedules, isLoading } = useAvailability();
  const { data: bookingSettings } = useBookingSettings();
  const createSchedule = useCreateSchedule();
  const updateSchedule = useUpdateSchedule();
  const deleteSchedule = useDeleteSchedule();
  const toggleActive = useToggleScheduleActive();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<BookingAvailabilityResponseDto | null>(null);

  const handleAdd = () => {
    setEditingSchedule(null);
    setEditorOpen(true);
  };

  const handleEdit = (schedule: BookingAvailabilityResponseDto) => {
    setEditingSchedule(schedule);
    setEditorOpen(true);
  };

  const handleSave = (values: AvailabilityFormValues) => {
    const payload = {
      dayOfWeek: values.dayOfWeek as DayOfWeekEnum,
      startTime: `${values.startTime}:00`,
      endTime: `${values.endTime}:00`,
      isAvailable: values.isAvailable,
    };
    const onSuccess = () => setEditorOpen(false);

    if (editingSchedule) {
      updateSchedule.mutate({ id: editingSchedule.id, data: payload }, { onSuccess });
    } else {
      createSchedule.mutate(payload, { onSuccess });
    }
  };

  const handleDelete = (schedule: BookingAvailabilityResponseDto) => {
    if (window.confirm('Delete this schedule entry?')) {
      deleteSchedule.mutate(schedule.id);
    }
  };

  const handleToggle = (schedule: BookingAvailabilityResponseDto) => {
    toggleActive.mutate({
      id: schedule.id,
      data: {
        dayOfWeek: schedule.dayOfWeek,
        startTime: schedule.startTime,
        endTime: schedule.endTime,
        isAvailable: !schedule.isAvailable,
      },
    });
  };


  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-strivre-navy flex items-center gap-2"><Clock size={22} /> Availability Management</h1>
          <p className="text-sm text-strivre-mutedtext mt-1">Configure working days, opening hours, and appointment slots.</p>
        </div>
        <button onClick={handleAdd} className="flex items-center gap-2 bg-strivre-navy text-white rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-strivre-navy/90">
          <Plus size={18} /> Add Schedule
        </button>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Spinner size="h-8 w-8" />
          <p className="mt-3 text-strivre-mutedtext">Loading availability...</p>
        </div>
      ) : (
        <AvailabilityCalendar
          schedules={schedules ?? []}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleActive={handleToggle}
        />
      )}

      <AvailabilityEditor
        isOpen={editorOpen}
        schedule={editingSchedule}
        saving={createSchedule.isPending || updateSchedule.isPending}
        onClose={() => setEditorOpen(false)}
        onSave={handleSave}
      />
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">
            Availability Timezone
          </p>

          <p className="mt-1 text-sm text-amber-800">
            Current timezone: <strong>{bookingSettings?.timezone ?? 'UTC'}</strong>
          </p>

          <p className="mt-1 text-sm text-amber-700">
            Availability schedules are interpreted using this timezone.
          </p>

          <Link
            to="/booking/configuration"
            className="inline-flex mt-3 text-sm font-medium text-strivre-navy hover:underline"
          >
            Go to Booking Configuration →
          </Link>
        </div>
    </div>
  );
}
